// Phase 33 · 19b — the rules behind the ticket register.
//
// The screen's argument is that a list of tickets should be ordered by what it
// COSTS to ignore a row, not by when the row arrived. Two things have to be
// true for that to be honest, and both live here rather than in the component
// so they are testable:
//
//  1. The SLA figure must be the deadline that is still RUNNING. A ticket whose
//     first reply landed on time still has a first-response deadline in the
//     past; printing "3h over" against it would be a lie about a target that
//     was met. So the live target is the first unmet one, and the cell says
//     which dimension it belongs to.
//  2. The tabs are not a second filtering system. They are ordinary values in
//     the SAME `TicketFilters` the advanced panel edits, so a tab and the panel
//     can never disagree about what the list is showing.

import type { TicketFilters } from '@components/tickets/AdvancedFilters';
import { duration } from '@shared/format/duration';
import type { Ticket } from '@centraoneplus/shared-types';

export type TicketSlaTone = 'danger' | 'warn' | 'normal' | 'none';

export interface LiveSla {
  tone: TicketSlaTone;
  /** '2h 40m over' · '18m left' · '—'. Never '0m' for a ticket with no policy. */
  label: string;
  /** Which target the figure belongs to, or null when no clock is running. */
  dimension: 'first reply' | 'resolution' | null;
}

const NO_SLA: LiveSla = { tone: 'none', label: '—', dimension: null };

function at(value: unknown): number | null {
  if (typeof value !== 'string' || !value) return null;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? null : t;
}

/**
 * The deadline that is still running on this ticket.
 *
 * A resolved or closed ticket has no live clock at all — its targets were
 * either met or missed, and a countdown against it would read as work still
 * owed. Beyond that the first unmet target wins: first response while it is
 * still outstanding, otherwise resolution.
 */
export function liveSla(ticket: Ticket, now: number = Date.now()): LiveSla {
  const t = ticket as unknown as Record<string, unknown>;
  if (ticket.status === 'resolved' || ticket.status === 'closed') return NO_SLA;

  const frDue = at(t.firstResponseDueAt);
  const frAt = at(t.firstResponseAt);
  const resDue = at(t.resolutionDueAt);
  const resAt = at(t.resolvedAt);

  let due: number | null = null;
  let dimension: LiveSla['dimension'] = null;
  if (frDue !== null && frAt === null) {
    due = frDue;
    dimension = 'first reply';
  } else if (resDue !== null && resAt === null) {
    due = resDue;
    dimension = 'resolution';
  }
  if (due === null) return NO_SLA;

  const delta = due - now;
  const seconds = Math.abs(delta) / 1000;
  if (delta <= 0) return { tone: 'danger', label: `${duration(seconds)} over`, dimension };
  return {
    tone: delta <= 60 * 60 * 1000 ? 'warn' : 'normal',
    label: `${duration(seconds)} left`,
    dimension,
  };
}

// The page is NOT re-sorted in the browser. Ordering is the server's
// `sla_priority` (firstResponseDueAt, nulls last) so that page 2 continues
// where page 1 stopped; re-ranking the loaded rows by `liveSla` would make the
// visible order right and the pagination wrong. Where the two disagree — a
// ticket whose first reply landed, now counting down to resolution — the cell's
// dimension label is what explains the row's position.

// ----------------------------------------------------------------- the tabs

export type TicketTab = 'all' | 'unassigned' | 'breached' | 'mine' | 'waiting';

export const TICKET_TABS: TicketTab[] = ['all', 'unassigned', 'breached', 'mine', 'waiting'];

export const TAB_LABEL: Record<TicketTab, string> = {
  all: 'All',
  unassigned: 'Unassigned',
  breached: 'Breached',
  mine: 'Mine',
  waiting: 'Waiting',
};

/**
 * Every key a tab can own. A tab switch clears all of them before writing its
 * own, so switching from Waiting to Mine does not leave a status filter behind
 * — the same reason the advanced panel has FILTER_RESET_KEYS.
 */
const TAB_KEYS = ['assigneeIds', 'assignedToMe', 'slaStatus', 'status'] as const;

function tabValues(tab: TicketTab): Partial<TicketFilters> {
  switch (tab) {
    case 'unassigned':
      return { assigneeIds: ['unassigned'] };
    case 'breached':
      return { slaStatus: 'breached' };
    case 'mine':
      return { assignedToMe: true };
    case 'waiting':
      return { status: ['pending'] };
    case 'all':
    default:
      return {};
  }
}

/** Apply a tab on top of the panel's other filters, leaving them intact. */
export function applyTab(current: TicketFilters, tab: TicketTab): TicketFilters {
  const next: TicketFilters = { ...current };
  for (const key of TAB_KEYS) delete next[key];
  return { ...next, ...tabValues(tab) };
}

/**
 * Which tab the current filters represent, or null when the panel has been
 * driven somewhere no tab describes. Returning null — rather than falsely
 * lighting up ALL — is what stops the strip claiming to show everything while
 * a filter quietly narrows the list.
 */
const LINKABLE_STATUSES = ['new', 'in_progress', 'pending', 'resolved', 'closed'];

/**
 * The list the nav rail's Tickets badge counts — my tickets waiting on me —
 * as a link. Must stay in step with the BFF's `waitingTicketsQuery` (nav
 * counts), so the number on the badge is the number of rows it opens.
 */
export const TICKETS_WAITING_ON_ME_PATH = '/tickets/list?assignee=me&status=new,in_progress';

/**
 * Filters a link can open the list with: `assignee=me` and a comma-separated
 * `status`. Unknown values are ignored; null when the URL carries none, so the
 * page falls back to its usual default.
 */
export function filtersFromSearchParams(params: URLSearchParams): TicketFilters | null {
  const filters: TicketFilters = {};
  if (params.get('assignee') === 'me') filters.assignedToMe = true;
  const status = (params.get('status') ?? '')
    .split(',')
    .filter((s) => LINKABLE_STATUSES.includes(s));
  if (status.length) filters.status = status;
  return Object.keys(filters).length ? filters : null;
}

export function activeTab(f: TicketFilters): TicketTab | null {
  const one = (v?: string[], value?: string) => v?.length === 1 && v[0] === value;
  if (f.assignedToMe) return 'mine';
  if (one(f.assigneeIds, 'unassigned')) return 'unassigned';
  if (f.slaStatus === 'breached') return 'breached';
  if (one(f.status, 'pending')) return 'waiting';
  const touched = TAB_KEYS.some((k) => {
    const v = f[k];
    return Array.isArray(v) ? v.length > 0 : v !== undefined;
  });
  return touched ? null : 'all';
}

// ------------------------------------------------------------- row furniture

/**
 * Who the ticket is for. There is no organisation on a ticket — the BFF
 * enriches `customer` with id/name/email/phone only — so the artboard's
 * "MERIDIAN BANK" has no field behind it and the person is printed instead.
 */
export function customerLabel(ticket: Ticket): string | null {
  const t = ticket as unknown as Record<string, any>;
  const value =
    t.contactName || t.customer?.name || t.contactEmail || t.customer?.email || null;
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

/** 'Quote · Approval'. Either half may be missing; both missing renders '—'. */
export function pipelineStage(ticket: Ticket): string | null {
  const t = ticket as unknown as Record<string, any>;
  const parts = [t.pipeline?.name, t.stage?.name].filter(
    (p): p is string => typeof p === 'string' && p.trim().length > 0
  );
  return parts.length ? parts.join(' · ') : null;
}
